import type { ComponentType, ReactNode } from 'react';
import type { FieldError } from 'react-hook-form';
import { Box, Fade, Skeleton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type { DateValidationError } from '@mui/x-date-pickers';
import { useFormContext } from 'react-hook-form';

import type {
  AddressFieldConfig,
  CheckboxFieldConfig,
  DateFieldConfig,
  FieldConfig,
  MoneyFieldConfig,
  OptionBoxFieldConfig,
  SelectFieldConfig,
  SwitchFieldConfig,
  TextFieldConfig,
  TextReadOnlyFieldConfig,
} from '../lease-details.config';
import type { FieldEventContext, FieldRenderProps, SectionKey } from '../lease-details.types';

import { LeaseAddressInput } from '../inputs/lease-address-input';
import { LeaseCheckboxInput } from '../inputs/lease-checkbox-input';
import { LeaseDatePicker } from '../inputs/lease-date-input';
import { LeaseDetailsSelect } from '../inputs/lease-details-select';
import { LeaseMoneyInput } from '../inputs/lease-money-input';
import { LeaseOptionBoxInput } from '../inputs/lease-option-box';
import { LeaseSwitchInput } from '../inputs/lease-switch-input';
import { LeaseTextInput, LeaseTextReadOnlyInput } from '../inputs/lease-text-input';
import { useLeaseDetailsUI } from '../lease-details-ui.context';
import { useResolvedOptions, useVisibilityRule } from '../hooks';
import { createDataTestId } from '@/utils/createDataTestId';

// Per-field dispatch layer. Internal — not re-exported from the package barrel.
// Section components hand it a `fields` map; it resolves rules against form
// state and dispatches each field by `kind` (or to a `component` override).

type FieldEntry = {
  name: string;
  field: FieldConfig;
};

export type FieldsRendererProps = {
  fields: Record<string, FieldConfig>;
  sectionKey: SectionKey;
  // When the renderer is mounted inside the applicants array editor, this is
  // the row index. Used to build dotted RHF paths like `applicants.0.email`.
  arrayIndex?: number;
  // True when an ancestor (e.g. the applicants primary-locked row) forces all
  // fields to render in the disabled state. OR'd with the field's own
  // `disabled` rule.
  inheritedDisabled?: boolean;
};

const buildPath = (sectionKey: SectionKey, name: string, arrayIndex?: number): string =>
  arrayIndex == null ? name : `${sectionKey}.${arrayIndex}.${name}`;

export const FieldsRenderer = ({
  fields,
  sectionKey,
  arrayIndex,
  inheritedDisabled = false,
}: FieldsRendererProps) => {
  const entries: FieldEntry[] = Object.entries(fields).map(([name, field]) => ({ name, field }));

  // Collapse consecutive same-`rowGroup` fields into one row; everything else is
  // its own full-width row. Fields are spaced 16px (matches the legacy form).
  const rows: FieldEntry[][] = [];
  for (const entry of entries) {
    const group = entry.field.rowGroup;
    const previous = rows[rows.length - 1];
    if (group && previous && previous[0].field.rowGroup === group) {
      previous.push(entry);
    } else {
      rows.push([entry]);
    }
  }

  const renderField = ({ name, field }: FieldEntry) => (
    <FieldDispatcher
      key={name}
      name={name}
      field={field}
      sectionKey={sectionKey}
      arrayIndex={arrayIndex}
      inheritedDisabled={inheritedDisabled}
    />
  );

  return (
    <Stack direction="column" spacing={2}>
      {rows.map((row) =>
        row.length > 1 ? (
          <Stack
            key={row.map((entry) => entry.name).join('-')}
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 4 }}
            // hidden fields render `null`, so they leave no empty column.
            sx={{ '& > *': { flex: 1, minWidth: 0 } }}
          >
            {row.map(renderField)}
          </Stack>
        ) : (
          renderField(row[0])
        ),
      )}
    </Stack>
  );
};

type FieldDispatcherProps = {
  name: string;
  field: FieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  inheritedDisabled: boolean;
};

const FieldDispatcher = ({
  name,
  field,
  sectionKey,
  arrayIndex,
  inheritedDisabled,
}: FieldDispatcherProps) => {
  const path = buildPath(sectionKey, name, arrayIndex);
  const isVisible = useVisibilityRule(field.visible);
  const requiredResolved = useVisibilityRule(field.required ?? false);
  const disabledResolved = useVisibilityRule(field.disabled ?? false);
  const disabled = inheritedDisabled || disabledResolved;

  const renderInput = (): ReactNode => {
    if (field.component) {
      // Cast to the unknown-typed render-props shape so the discriminated union
      // doesn't force `defaultValue` into the intersection of every variant's
      // value type. The consumer's component declares its own TValue and reads
      // `defaultValue` accordingly.
      const Override = field.component as ComponentType<FieldRenderProps>;
      return (
        <Override
          name={path}
          sectionKey={sectionKey}
          arrayIndex={arrayIndex}
          required={requiredResolved}
          disabled={disabled}
          label={field.label}
          placeholder={field.placeholder}
          helperText={field.helperText}
          defaultValue={field.defaultValue}
        />
      );
    }

    return renderByKind();
  };

  // Fade fields animate in on appear only; when the rule hides them they're
  // removed immediately (no fade-out), matching the legacy form's
  // conditionally-rendered `<Fade in>`.
  if (field.fade) {
    return isVisible ? (
      <Fade in appear>
        <Box>{renderInput()}</Box>
      </Fade>
    ) : null;
  }

  if (!isVisible) return null;
  return renderInput();

  function renderByKind(): ReactNode {
    switch (field.kind) {
      case 'text':
        return (
          <TextField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
          />
        );
      case 'money':
        return (
          <MoneyField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
          />
        );
      case 'date':
        return (
          <DateField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
          />
        );
      case 'select':
        return (
          <SelectField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
          />
        );
      case 'text-readonly':
        return <ReadOnlyField path={path} field={field} />;
      case 'option-box':
        return (
          <OptionBoxField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
          />
        );
      case 'switch':
        return (
          <SwitchField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            disabled={disabled}
          />
        );
      case 'checkbox':
        return (
          <CheckboxField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            disabled={disabled}
          />
        );
      case 'address':
        return (
          <AddressField
            path={path}
            field={field}
            sectionKey={sectionKey}
            arrayIndex={arrayIndex}
            required={requiredResolved}
            disabled={disabled}
          />
        );
      default:
        return null;
    }
  }
};

const useFieldEventContextBuilder = <TValue,>(
  path: string,
  sectionKey: SectionKey,
  arrayIndex?: number,
) => {
  const { getFieldState, formState, getValues } = useFormContext();
  return (value: TValue, optionId?: string): FieldEventContext<TValue> => ({
    value,
    valid: !getFieldState(path, formState).invalid,
    fieldPath: path,
    arrayIndex,
    sectionKey,
    optionId,
    getValues: (name?: string) => (name == null ? getValues() : getValues(name)),
  });
};

type TextFieldProps = {
  path: string;
  field: TextFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
};

const TextField = ({ path, field, sectionKey, arrayIndex, required, disabled }: TextFieldProps) => {
  const buildContext = useFieldEventContextBuilder<string>(path, sectionKey, arrayIndex);
  const handleBlur = (value: string | undefined) => {
    // Form state can be `undefined` (untouched) or `null` (seeded default);
    // coerce to '' so the callback receives the `FieldEventContext<string>`
    // its `TextFieldConfig` (extends FieldConfigBase<string>) type promises.
    field.events?.onBlur?.(buildContext(value ?? ''));
  };
  const handleChange = (value: string) => {
    field.events?.onChange?.(buildContext(value));
  };

  return (
    <LeaseTextInput
      name={path}
      title={field.label ?? ''}
      placeholder={field.placeholder}
      helperText={field.helperText}
      required={required}
      disabled={disabled}
      type={field.inputType}
      onBlurValue={field.events?.onBlur ? handleBlur : undefined}
      onChangeValue={field.events?.onChange ? handleChange : undefined}
    />
  );
};

type MoneyFieldProps = {
  path: string;
  field: MoneyFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
};

const MoneyField = ({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
}: MoneyFieldProps) => {
  const buildContext = useFieldEventContextBuilder<string>(path, sectionKey, arrayIndex);
  const handleBlur = (value: string | undefined) => {
    // See `TextField` — coerce to '' so the callback honors its
    // `FieldEventContext<string>` contract.
    field.events?.onBlur?.(buildContext(value ?? ''));
  };
  const handleChange = (value: string) => {
    field.events?.onChange?.(buildContext(value));
  };

  return (
    <LeaseMoneyInput
      name={path}
      title={field.label ?? ''}
      placeholder={field.placeholder}
      helperText={field.helperText}
      required={required}
      disabled={disabled}
      fixedDecimalScale={field.fixedDecimalScale}
      onBlurValue={field.events?.onBlur ? handleBlur : undefined}
      onChangeValue={field.events?.onChange ? handleChange : undefined}
    />
  );
};

type DateFieldProps = {
  path: string;
  field: DateFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
};

const DateField = ({ path, field, sectionKey, arrayIndex, required, disabled }: DateFieldProps) => {
  const { watch, setValue, trigger, getFieldState, formState } = useFormContext();
  const { inputSize: size } = useLeaseDetailsUI();

  const raw = watch(path) as string | null | undefined;
  const parsed = raw ? dayjs(raw) : null;
  const value = parsed?.isValid() ? parsed : null;
  const error = getFieldState(path, formState).error as FieldError | undefined;

  const buildContext = useFieldEventContextBuilder<string | null>(path, sectionKey, arrayIndex);

  const handleChange = (next: dayjs.Dayjs | null) => {
    if (next !== null && !next.isValid()) return;
    const stored = next ? next.toISOString() : null;
    setValue(path, stored, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    trigger(path);
    field.events?.onChange?.(buildContext(stored));
  };

  const handleError = (_err: DateValidationError) => {
    // Validation state is owned by the resolver — no value capture here.
    // `handleChange` already writes valid values with the right RHF flags.
  };

  const minDate = field.minDateOffsetDays
    ? dayjs().subtract(field.minDateOffsetDays, 'day')
    : undefined;
  const maxDate = field.maxDateOffsetDays ? dayjs().add(field.maxDateOffsetDays, 'day') : undefined;

  const dataTestId = createDataTestId({ inputString: path, suffix: 'input' });

  return (
    <LeaseDatePicker
      title={field.label ?? ''}
      value={value}
      onChange={handleChange}
      onError={handleError}
      error={error}
      required={required}
      disabled={disabled}
      dataTestId={dataTestId}
      minDate={minDate}
      maxDate={maxDate}
      size={size}
      redAsterisk
    />
  );
};

type SelectFieldProps = {
  path: string;
  field: SelectFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
};

const SelectField = ({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
}: SelectFieldProps) => {
  const { options, isLoading, isError } = useResolvedOptions(field.options);
  const buildContext = useFieldEventContextBuilder<string>(path, sectionKey, arrayIndex);

  if (isLoading) {
    return <Skeleton variant="rounded" height={48} width="100%" />;
  }

  if (isError) {
    return (
      <Stack direction="column" spacing={0.5}>
        {field.label && <Typography variant="body2">{field.label}</Typography>}
        <Typography variant="body2" color="error">
          Failed to load options. Please try again.
        </Typography>
      </Stack>
    );
  }

  return (
    <LeaseDetailsSelect
      name={path}
      title={field.label ?? ''}
      helperText={field.helperText}
      options={options}
      SelectItem={field.SelectItem}
      emptyOption={field.emptyOption}
      required={required}
      disabled={disabled}
      defaultValue={field.defaultValue}
      onSelect={(value) => field.events?.onChange?.(buildContext(value))}
    />
  );
};

type ReadOnlyFieldProps = {
  path: string;
  field: TextReadOnlyFieldConfig;
};

// Displayed value tracks form state (seeded with `field.value` at mount via
// `collectFieldDefaults`). No `?? field.value` fallback — display and submit
// must agree, including for the edge case where a consumer explicitly
// overrides via `initialValues`.
const ReadOnlyField = ({ path, field }: ReadOnlyFieldProps) => {
  const { watch } = useFormContext();
  const formValue = watch(path) as string | null | undefined;
  return (
    <LeaseTextReadOnlyInput
      name={path}
      title={field.label ?? ''}
      label={field.label ?? ''}
      value={formValue ?? ''}
    />
  );
};

type OptionBoxFieldProps = {
  path: string;
  field: OptionBoxFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
};

const OptionBoxField = ({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
}: OptionBoxFieldProps) => {
  const buildContext = useFieldEventContextBuilder<string>(path, sectionKey, arrayIndex);
  return (
    <LeaseOptionBoxInput
      name={path}
      options={field.options}
      label={field.label}
      companionIdField={field.companionIdField}
      layout={field.layout}
      useDrawerStyles={field.useDrawerStyles}
      skeletonCount={field.skeletonCount}
      required={required}
      disabled={disabled}
      onSelect={
        field.events?.onChange
          ? ({ value, optionId }) => field.events?.onChange?.(buildContext(value, optionId))
          : undefined
      }
    />
  );
};

type SwitchFieldProps = {
  path: string;
  field: SwitchFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  disabled: boolean;
};

const SwitchField = ({ path, field, sectionKey, arrayIndex, disabled }: SwitchFieldProps) => {
  const buildContext = useFieldEventContextBuilder<boolean>(path, sectionKey, arrayIndex);
  return (
    <LeaseSwitchInput
      name={path}
      label={field.label}
      disabled={disabled}
      onChangeValue={
        field.events?.onChange
          ? (checked) => field.events?.onChange?.(buildContext(checked))
          : undefined
      }
    />
  );
};

type CheckboxFieldProps = {
  path: string;
  field: CheckboxFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  disabled: boolean;
};

const CheckboxField = ({ path, field, sectionKey, arrayIndex, disabled }: CheckboxFieldProps) => {
  const buildContext = useFieldEventContextBuilder<boolean>(path, sectionKey, arrayIndex);
  return (
    <LeaseCheckboxInput
      name={path}
      label={field.label}
      disabled={disabled}
      onChangeValue={
        field.events?.onChange
          ? (checked) => field.events?.onChange?.(buildContext(checked))
          : undefined
      }
    />
  );
};

type AddressFieldProps = {
  path: string;
  field: AddressFieldConfig;
  sectionKey: SectionKey;
  arrayIndex?: number;
  required: boolean;
  disabled: boolean;
};

const AddressField = ({
  path,
  field,
  sectionKey,
  arrayIndex,
  required,
  disabled,
}: AddressFieldProps) => {
  const buildContext = useFieldEventContextBuilder<unknown>(path, sectionKey, arrayIndex);
  return (
    <LeaseAddressInput
      name={path}
      label={field.label}
      required={required}
      disabled={disabled}
      showLoadingSkeleton={field.showLoadingSkeleton}
      onSelect={
        field.events?.onChange
          ? (option) => field.events?.onChange?.(buildContext(option))
          : undefined
      }
    />
  );
};
